import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PostCard from '../components/PostCard';
import { Search } from 'lucide-react';
import { getPostsByCategory, getAllCategories } from '../data/posts';

const CATEGORIES = getAllCategories();

export default function Home() {
  const [category, setCategory] = useState('All');

  const posts = useMemo(() => {
    const filtered = getPostsByCategory(category);
    // Sort by date descending
    return [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [category]);

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-border">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-2xl animate-in">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-5">
              Tech Blog
            </p>
            <h1
              className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.05] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Dev<span>Blog</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
              Practical notes on Azure cloud solutions, DevOps practices, Automation, Infrastructure as Code, Backend development and Frontend engineering.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <Link
                to="/"
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/images/vp-engineer-logo.png" 
                  alt="VP Engineer" 
                  className="h-10 w-auto dark:invert"
                />
              </Link>
            </div>
            <p className="mt-6 text-sm text-muted-foreground flex items-center gap-2">
              <Search className="h-3.5 w-3.5" />
              Press{' '}
              <kbd className="bg-muted border border-border px-1.5 py-0.5 rounded text-xs font-mono">
                ⌘K
              </kbd>{' '}
              to search posts
            </p>
          </div>
        </div>
      </section>

      {/* Posts Section */}
      <section className="max-w-screen-xl mx-auto px-6 md:px-12 py-12">
        {/* Category Filter */}
        <div
          data-testid="category-filter"
          className="flex flex-wrap gap-2 mb-10"
        >
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              data-testid={`filter-${cat}`}
              onClick={() => setCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                category === cat
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40 bg-transparent'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div data-testid="no-posts" className="text-center py-24">
            <p className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No posts yet
            </p>
            <p className="text-muted-foreground">
              {category === 'All'
                ? 'Check back soon for new content.'
                : `No posts in the ${category} category yet.`}
            </p>
          </div>
        ) : (
          <div
            data-testid="posts-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {posts.map((post, i) => (
              <div
                key={post.id}
                className="animate-in"
                style={{ animationDelay: `${i * 40}ms`, animationFillMode: 'both' }}
              >
                <PostCard post={post} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
